import React from 'react';
import {CallToAction} from '@contentlab/api/avatars/types';
import translate from '@/utils/translate';
import {
  CUSTOM_GOAL_VALUE,
  GOAL_OPTIONS,
  isCustomGoal,
} from '../../../constants/goalOptions';
import ProjectBasicsForm from '../../shared/ProjectBasicsForm';

interface ProjectBasicsSectionProps {
  testID?: string;
  promoting: string;
  onPromotingChange: (value: string) => void;
  selectedGoal: CallToAction | string | null; // Can be enum or custom string
  onGoalSelect: (goal: CallToAction | string | null) => void;
  customGoal: string;
  onCustomGoalAccept: (customGoalText: string) => void;
  onCustomGoalDelete: () => void;
  onCustomGoalPopperOpen: (event?: React.MouseEvent<HTMLElement>) => void;
  isCustomGoalPopperOpen: boolean;
  onCustomGoalPopperClose: () => void;
  customGoalAnchorEl: HTMLElement | null;
  // Section-specific labels
  promotingLabel?: string;
  promotingPlaceholder?: string;
  goalLabel?: string;
}

const MAX_PROMOTING_LENGTH = 50;

function ProjectBasicsSection({
  testID = 'project-basics-section',
  promoting,
  onPromotingChange,
  selectedGoal,
  onGoalSelect,
  customGoal,
  onCustomGoalAccept,
  onCustomGoalDelete,
  onCustomGoalPopperOpen,
  isCustomGoalPopperOpen,
  onCustomGoalPopperClose,
  customGoalAnchorEl,
  promotingLabel = translate('What are you promoting?'),
  promotingPlaceholder = translate(
    'New feature launch, Spring sale, Learning app'
  ),
  goalLabel = translate("What's the primary goal for this avatar video?"),
}: ProjectBasicsSectionProps) {
  // If selectedGoal is a custom string (not a CallToAction enum value),
  // map it back to CUSTOM_GOAL_VALUE so the "Other" chip shows as selected
  const isCustomGoalValue = isCustomGoal(selectedGoal);

  const selectedValues = selectedGoal
    ? [isCustomGoalValue ? CUSTOM_GOAL_VALUE : selectedGoal]
    : [];

  const handleGoalSelect = (value: string) => {
    if (value !== CUSTOM_GOAL_VALUE) {
      // Regular enum value - pass it through
      onGoalSelect(value as CallToAction);
    }
    // If value === CUSTOM_GOAL_VALUE we don't need to do anything since the custom value has
    // already been accepted by onCustomValueAccept.
  };

  return (
    <ProjectBasicsForm
      testID={testID}
      // Text input props
      showTextInput
      promoting={promoting}
      onPromotingChange={onPromotingChange}
      promotingLabel={promotingLabel}
      promotingPlaceholder={promotingPlaceholder}
      maxCharacters={MAX_PROMOTING_LENGTH}
      showCharacterCounter
      // Chip selection props
      chipOptions={GOAL_OPTIONS}
      selectedValues={selectedValues}
      onValueSelect={handleGoalSelect}
      chipSectionLabel={goalLabel}
      // Custom option props
      showCustomOption
      customId={CUSTOM_GOAL_VALUE}
      customValue={customGoal}
      onCustomValueAccept={onCustomGoalAccept}
      onCustomValueDelete={onCustomGoalDelete}
      onCustomPopperOpen={onCustomGoalPopperOpen}
      isCustomPopperOpen={isCustomGoalPopperOpen}
      onCustomPopperClose={onCustomGoalPopperClose}
      customPopperAnchorEl={customGoalAnchorEl}
    />
  );
}

export default ProjectBasicsSection;
